import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarOff, Clock, CheckCheck, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/api/client";

const fmt = (iso) => { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y.slice(2)}`; };
const DONE_MSG = { later_2h: "Will remind you in 2 hours", later_tomorrow: "Will remind you tomorrow at 10 AM", handled: "Marked as handled", not_applicable: "Dismissed — not for you" };

// Floating reminder for a leave request awaiting this user's approval.
export function LeaveNudge({ leave, onChanged }) {
  const nav = useNavigate();
  const [later, setLater] = useState(false);
  const act = async (action) => {
    try {
      await api.post(`/leaves/${leave.id}/nudge`, { action });
      toast.success(DONE_MSG[action]); onChanged();
    } catch (x) { toast.error(formatApiError(x?.response?.data?.detail) || "Failed"); }
  };
  const stage = leave.status === "awaiting_hr" ? "HR approval" : "Manager approval";
  const btn = "h-8 px-2.5 rounded-md border border-white/15 text-xs inline-flex items-center gap-1 hover:bg-white/5";
  return (
    <div className="rounded-xl border border-sky-500/40 bg-surface shadow-float p-3 animate-fade-in-up" data-testid={`nudge-leave-${leave.id}`}>
      <div className="flex items-start gap-2">
        <CalendarOff size={16} className="text-sky-500 mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate">{leave.user_name} applied for leave</div>
          <div className="text-xs text-white/70 truncate">{leave.type_label} · {leave.days} day{leave.days === 1 ? "" : "s"} · {fmt(leave.from_date)}–{fmt(leave.to_date)}</div>
          <div className="text-xs mt-0.5 text-sky-600 dark:text-sky-400">Awaiting your {stage.toLowerCase()}</div>
        </div>
      </div>
      {later ? (
        <div className="flex gap-1.5 mt-2" data-testid={`nudge-leave-later-menu-${leave.id}`}>
          <button type="button" onClick={() => act("later_2h")} data-testid={`nudge-leave-later-2h-${leave.id}`} className={btn}>In 2 hours</button>
          <button type="button" onClick={() => act("later_tomorrow")} data-testid={`nudge-leave-later-tomorrow-${leave.id}`} className={btn}>Tomorrow 10 AM</button>
          <button type="button" onClick={() => setLater(false)} data-testid={`nudge-leave-later-back-${leave.id}`} className={btn}>Back</button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 mt-2">
          <button type="button" onClick={() => nav("/leaves?tab=approvals")} data-testid={`nudge-leave-review-${leave.id}`} className="h-8 px-2.5 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold inline-flex items-center gap-1"><Eye size={13} /> Review</button>
          <button type="button" onClick={() => setLater(true)} data-testid={`nudge-leave-later-${leave.id}`} className={btn}><Clock size={13} /> Remind me later</button>
          <button type="button" onClick={() => act("handled")} data-testid={`nudge-leave-handled-${leave.id}`} title="Already dealt with — don't remind again" className={btn}><CheckCheck size={13} /> Handled</button>
          <button type="button" onClick={() => act("not_applicable")} data-testid={`nudge-leave-na-${leave.id}`} title="Not mine to approve — don't remind again" className={btn}><XCircle size={13} /> Not for me</button>
        </div>
      )}
    </div>
  );
}
